import {
  ICellRendererParams,
  ValueGetterParams,
} from "@ag-grid-community/core";
import { TFunction } from "i18next";

import {
  DateRenderer,
  StatusRenderer,
} from "fe-shared-transactions/src/components/TransactionsDataGrid/renderers";

import { InventoryAdjustmentStatusCode } from "../../../types/inventoryAdjustments";

import ParticipantRenderer from "../renderers/ParticipantRenderer";
import InventoryAdjustmentNumberRenderer from "../renderers/InventoryAdjustmentNumberRenderer";
import AssigneeRenderer from "../renderers/AssigneeRenderer";

export const BULK_ASSIGN_STATUSES = [
  InventoryAdjustmentStatusCode.Submitted,
  InventoryAdjustmentStatusCode.InReview,
  InventoryAdjustmentStatusCode.Reviewed,
];

const getStatusFilterOptions = (t: TFunction) =>
  Object.values(InventoryAdjustmentStatusCode).map((item) => ({
    value: item,
    label: t(`inventoryAdjustment.statuses.${item}`),
  }));

export const getColumnDefs = (t: TFunction) => [
  {
    headerName: "",
    sortable: false,
    field: "selection",
    width: 30,
    minWidth: 30,
    checkboxSelection: true,
    headerCheckboxSelection: true,
    headerCheckboxSelectionFilteredOnly: false,
  },
  {
    headerName: t("inventoryAdjustment.grid.cols.completedDate"),
    field: "completedDate",
    width: 200,
    minWidth: 200,
    cellRenderer: "completedDateRenderer",
    filter: "dateFilter",
    floatingFilterComponent: "dateRangeFilter",
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
  {
    headerName: t("inventoryAdjustment.grid.cols.inventoryAdjustmentNumber"),
    field: "overallAdjustmentNumber",
    cellRenderer: "inventoryAdjustmentNumberRenderer",
    width: 170,
    minWidth: 170,
    filter: "textFilter",
    filterParams: {
      startFilteringThreshold: 1,
    },
    floatingFilterComponent: "floatingTextFilter",
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    valueGetter: (params: ValueGetterParams) => ({
      overallAdjustmentNumber: params.data?.overallAdjustmentNumber,
    }),
  },
  {
    headerName: t("inventoryAdjustment.grid.cols.participants"),
    field: "displayNameOrRegNumber",
    minWidth: 225,
    flex: 1,
    cellRenderer: "participantRenderer",
    filter: "textFilter",
    filterParams: {
      startFilteringThreshold: 1,
    },
    floatingFilterComponent: "floatingTextFilter",
    floatingFilterComponentParams: {
      placeholder: t("inventoryAdjustment.grid.placeholder.participant"),
      suppressFilterButton: true,
    },
  },
  {
    headerName: t("inventoryAdjustment.grid.cols.createdBy"),
    field: "createdBy",
    width: 190,
    minWidth: 190,
    flex: 1,
    filter: "textFilter",
    filterParams: {
      startFilteringThreshold: 1,
    },
    floatingFilterComponent: "floatingTextFilter",
    floatingFilterComponentParams: {
      placeholder: t("inventoryAdjustment.grid.placeholder.createBy"),
      suppressFilterButton: true,
    },
  },
  {
    headerName: t("transactions.grid.cols.statusCode"),
    colId: "statusCode",
    width: 160,
    minWidth: 160,
    valueGetter: (params: ValueGetterParams) => ({
      statusCode: params.data?.statusCode,
      isAdjustable: params.data?.isAdjustable,
    }),
    cellRenderer: "statusRenderer",
    filter: "selectFilter",
    floatingFilterComponent: "multiSelectFilter",
    floatingFilterComponentParams: {
      id: "status-multi-select-filter",
      width: 200,
      suppressFilterButton: true,
      options: getStatusFilterOptions(t),
    },
  },
  {
    headerName: t("inventoryAdjustment.grid.cols.assignee"),
    colId: "assigneeDisplayName",
    valueGetter: (params: ValueGetterParams) => ({
      statusCode: params.data?.statusCode,
      assignee: params.data?.assignee,
    }),
    width: 230,
    minWidth: 200,
    cellRenderer: "assigneeRenderer",
    filter: "textFilter",
    floatingFilterComponent: "floatingTextFilter",
    floatingFilterComponentParams: {
      placeholder: t("inventoryAdjustment.grid.placeholder.assignee"),
      suppressFilterButton: true,
    },
  },
  {
    headerName: "",
    sortable: false,
    field: "flags",
    filter: "textFilter",
    floatingFilterComponent: "clearFilters",
    floatingFilterComponentParams: {
      suppressFilterButton: true,
      id: "transactions-clear-filters-button",
    },
    width: 40,
    minWidth: 40,
  },
];

export const getRenderers = (canAssign: boolean) => ({
  completedDateRenderer: DateRenderer,
  participantRenderer: ParticipantRenderer,
  statusRenderer: StatusRenderer,
  inventoryAdjustmentNumberRenderer: InventoryAdjustmentNumberRenderer,
  assigneeRenderer: (props: ICellRendererParams) =>
    AssigneeRenderer({ ...props, canAssign }),
});

export default {
  getColumnDefs,
  getRenderers,
  BULK_ASSIGN_STATUSES,
};
